import { useHandler } from "@redotech/react-util/hook";
import { useCallback, useEffect, useRef, useState } from "react";

export interface VerticalScrollEdges {
  /** Whether there is anything to scroll to at all. */
  scrollable: boolean;
  atStart: boolean;
  atEnd: boolean;
}

export interface VerticalScrollMaskResult {
  scrollRef: React.RefObject<HTMLDivElement>;
  /** Attach to the element wrapping the scrolled content to track its height. */
  contentRef: React.RefObject<HTMLDivElement>;
  handleScroll: () => void;
  edges: VerticalScrollEdges;
  maskImage: string | undefined;
}

const AT_START: VerticalScrollEdges = {
  scrollable: false,
  atStart: true,
  atEnd: true,
};

function sameEdges(a: VerticalScrollEdges, b: VerticalScrollEdges): boolean {
  return (
    a.scrollable === b.scrollable &&
    a.atStart === b.atStart &&
    a.atEnd === b.atEnd
  );
}

export function maskImageFor(edges: VerticalScrollEdges): string | undefined {
  if (!edges.scrollable) {
    return undefined;
  }
  if (edges.atStart) {
    return "linear-gradient(to bottom, black 0%, black 90%, transparent 100%)";
  }
  if (edges.atEnd) {
    return "linear-gradient(to bottom, transparent 0%, black 10%, black 100%)";
  }
  return "linear-gradient(to bottom, transparent 0%, black 10%, black 90%, transparent 100%)";
}

/**
 * Tracks which edges of a scroll container currently hide content, so callers
 * never have to infer the position back out of the mask.
 */
export function useVerticalScrollMask(): VerticalScrollMaskResult {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState<VerticalScrollEdges>(AT_START);

  const measure = useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const next: VerticalScrollEdges = {
      scrollable: element.scrollHeight - element.clientHeight > 1,
      atStart: element.scrollTop <= 0,
      atEnd:
        element.scrollTop + element.clientHeight >= element.scrollHeight - 1,
    };
    setEdges((previous) => (sameEdges(previous, next) ? previous : next));
  }, []);

  const handleScroll = useHandler(measure);

  useEffect(() => {
    const frame = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(frame);
  }, [measure]);

  // Content growing after mount can turn a container that fit into one that scrolls.
  useEffect(() => {
    const observed = contentRef.current ?? scrollRef.current;
    if (!observed || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(measure);
    observer.observe(observed);
    return () => observer.disconnect();
  }, [measure]);

  return {
    scrollRef,
    contentRef,
    handleScroll,
    edges,
    maskImage: maskImageFor(edges),
  };
}
